import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../../api/client';
import {
  ID_PROOF_RULES,
  ID_PROOF_TYPES,
  sanitizeIdNumber,
  useAppeal,
  useLogin,
  useRequestOtp,
  useSignup,
  useUploadAppealDoc,
  useUploadIdProof,
  useVerifyOtp,
  type AuthResult,
  type SuspendedInfo,
} from '../../api/queries/auth';
import type { Role } from '../../api/types';
import { PasswordField } from '../../components/PasswordField';
import { Select } from '../../components/Select';
import { img } from '../../content/imagery';
import { useT } from '../../i18n';
import { dayFmt } from '../../lib/format';
import { PHONE_LENGTH, isValidPhone, sanitizePhone } from '../../lib/phone';
import { rememberedPhone, setRememberedPhone } from '../../lib/remember';
import { focusFirstInvalid, useRecheckOnLanguage, useValidators } from '../../lib/validators';
import { useAuth } from '../../providers/AuthProvider';
import { useToast } from '../../providers/ToastProvider';

type Tab = 'login' | 'signup';
/** Signup is mobile-first: number → OTP → account details. */
type SignupStep = 'phone' | 'otp' | 'details';

export function LoginPage() {
  const { t } = useT();
  const { user } = useAuth();
  // ?signup=venue is the footer's "List your venue": straight to signing up,
  // with the venue-owner account already chosen.
  const [params] = useSearchParams();
  const forVenue = params.get('signup') === 'venue';
  const [tab, setTab] = useState<Tab>(forVenue ? 'signup' : 'login');
  const returnTo = useReturnTo();

  // The menu and footer link here while /login is already open; the page stays
  // mounted, so follow every navigation rather than reading the query once.
  // location.key changes even when the URL does not — someone already on
  // ?signup=venue who switched to "Log in" and clicks "List your venue" again
  // must still land on the venue sign-up.
  const { key: navKey } = useLocation();
  const arrived = useRef(false);
  useEffect(() => {
    if (!arrived.current) {
      arrived.current = true;
      if (forVenue) setTab('signup');
      return;
    }
    // A later link to this same page — the footer's "List your venue", the
    // header's "Log in" — opens the form it names, and brings it into view: the
    // page does not change, so without the scroll a click from the footer
    // switched the tab out of sight and looked like it had done nothing.
    setTab(forVenue ? 'signup' : 'login');
    // Instant: a smooth scroll is cut short by the form swapping underneath it.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [forVenue, navKey]);

  /**
   * Signed in already? Then there is nothing to sign into.
   *
   * /admin/login has gated this from the start and this door did not, so an
   * authenticated visitor who reached /login — from a stale tab, a bookmark, the
   * back button — was shown a "Welcome back" form asking them to do what they
   * had already done. Sent to whichever home their account has, the same
   * landings a fresh sign-in uses.
   */
  if (user) {
    const home = user.role === 'admin' ? '/admin'
      : user.role === 'manager' || user.role === 'supervisor' ? '/manager'
        : returnTo ?? '/';
    return <Navigate to={home} replace />;
  }

  const title = tab === 'login' ? t('authWelcomeBack') : t('authCreateAccount');
  // Sign-up has no lede: the "I want to" choice right below says the same thing.
  const lede = tab === 'login' ? t('authLoginSubtitle') : null;

  return (
    <div className="auth">
      {/* Editorial photo and the brand line; hidden on phones, as in the design. */}
      <div className="auth-media">
        <img
          src={img('weddingAisle', 1200, { h: 1500, q: 75 })}
          srcSet={`${img('weddingAisle', 720, { h: 900, q: 75 })} 720w, ${img('weddingAisle', 1200, { h: 1500, q: 75 })} 1200w`}
          sizes="50vw"
          alt=""
          {...{ fetchpriority: 'high' }}
        />
        <span className="kicker">{t('authMediaKicker')}</span>
        <blockquote>
          {t('authQuoteA')} <span className="accent">{t('authQuoteB')}</span>
        </blockquote>
        <p>{t('authMediaBody')}</p>
      </div>

      <div className="auth-panel">
        <div className="auth-box">
          <div className="fade-up" style={{ '--d': '.05s' } as React.CSSProperties}>
            <h1 id="auth-title">{title}</h1>
            {lede && (
              <div className="lede">
                <p>{lede}</p>
              </div>
            )}
          </div>

          <div className="seg fade-up" style={{ '--d': '.12s' } as React.CSSProperties} role="tablist" aria-label={t('authAccountTabs')}>
            <button
              type="button"
              role="tab"
              id="auth-tab-login"
              aria-selected={tab === 'login'}
              aria-controls="auth-pane"
              onClick={() => setTab('login')}
            >
              {t('authLogin')}
            </button>
            <button
              type="button"
              role="tab"
              id="auth-tab-signup"
              aria-selected={tab === 'signup'}
              aria-controls="auth-pane"
              onClick={() => setTab('signup')}
            >
              {t('authSignup')}
            </button>
          </div>

          <div id="auth-pane" role="tabpanel" aria-labelledby={tab === 'login' ? 'auth-tab-login' : 'auth-tab-signup'}>
            {tab === 'login' ? (
              <LoginForm />
            ) : (
              <SignupFlow key={forVenue ? `venue-${navKey}` : 'guest'} onDone={() => setTab('login')} initialRole={forVenue ? 'manager' : 'user'} />
            )}
          </div>
          {/* One form for guests and managers on purpose: the account carries
              the role, so a chooser would only be a way to pick the wrong one.
              Admins sign in at /admin/login, deliberately not linked here. */}
        </div>
      </div>
    </div>
  );
}

/**
 * Where a booker was headed before being asked to sign in (RequireAuth puts it
 * in the route state), or null. Only a path on this site is honoured, so a
 * crafted link cannot bounce someone to another domain after they log in.
 */
function useReturnTo(): string | null {
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') && !from.startsWith('/login')
    ? from
    : null;
}

/** Shared landing after a successful login or signup. */
function useFinishAuth() {
  const { setUser } = useAuth();
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const returnTo = useReturnTo();

  return (result: AuthResult, onPendingApproval?: () => void) => {
    if (result.pendingApproval) {
      toast(t('authManagerPending'), 'good');
      onPendingApproval?.();
      return;
    }
    setUser(result.user);
    toast(t('authWelcomeName', { name: result.user.name.split(' ')[0] }), 'good');
    // Admins never arrive here — this door is scope:'public', and signup only
    // ever mints a booker or a manager. A supervisor (created by a manager)
    // lands on the same dashboard, which shows them a calendar-only view.
    const isStaff = result.user.role === 'manager' || result.user.role === 'supervisor';
    navigate(isStaff ? '/manager' : returnTo ?? '/', { replace: Boolean(returnTo) });
  };
}

function LoginForm() {
  const { t } = useT();
  const v = useValidators();
  // A remembered number refills the form, which is the whole point of the box:
  // sign out, come back, and only the password is left to type.
  const [phone, setPhone] = useState(rememberedPhone);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => Boolean(rememberedPhone()));
  const [errors, setErrors] = useState<{ phone?: string | null; password?: string | null }>({});
  useRecheckOnLanguage(setErrors, () => ({ phone: v.phone(phone), password: v.password(password) }));
  const [suspended, setSuspended] = useState<SuspendedInfo | null>(null);
  // Seconds the sign-in code is good for, once the server has asked this
  // account to confirm its number (a new supervisor's first sign-in).
  const [phoneCheck, setPhoneCheck] = useState<number | null>(null);
  const login = useLogin();
  const toast = useToast();
  const finishAuth = useFinishAuth();

  /**
   * Re-checks a field on every keystroke, but only once it has already failed.
   *
   * That is the two halves of the rule: nothing is flagged while the visitor is
   * still typing their number for the first time, and once something has been
   * flagged the message goes the instant the entry becomes correct — rather
   * than surviving until the next submit.
   */
  const revalidate = (field: 'phone' | 'password', value: string) => {
    setErrors((e) => {
      if (!e[field]) return e;
      return { ...e, [field]: field === 'phone' ? v.phone(value) : v.password(value) };
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    const found = { phone: v.phone(phone), password: v.password(password) };
    if (found.phone || found.password) {
      setErrors(found);
      focusFirstInvalid(e.currentTarget as HTMLFormElement);
      return;
    }
    setErrors({});

    try {
      const result = await login.mutateAsync({ phone: phone.trim(), password, scope: 'public' });
      // Only once the number is known good — remembering a typo is worse than
      // remembering nothing. Unticking clears it, so an earlier remembered
      // login cannot refill the form on a shared machine.
      setRememberedPhone(remember ? phone.trim() : '');
      finishAuth(result);
    } catch (err) {
      // A suspended account gets the full explanation panel rather than a toast
      // that vanishes before it can be read.
      const info = err instanceof ApiError ? (err.body.suspended as SuspendedInfo | undefined) : undefined;
      if (info) setSuspended(info);
      else if (err instanceof ApiError && err.body.verifyPhone && err.body.expiresInSec) {
        setPhoneCheck(Number(err.body.expiresInSec));
      } else toast((err as Error).message, 'bad');
    }
  };

  if (phoneCheck != null) {
    return (
      <PhoneCheckPanel
        phone={phone.trim()}
        password={password}
        expiresInSec={phoneCheck}
        onDone={(result) => {
          setRememberedPhone(remember ? phone.trim() : '');
          finishAuth(result);
        }}
        onBack={() => {
          setPhoneCheck(null);
          setPassword('');
        }}
      />
    );
  }

  if (suspended) {
    return (
      <SuspendedPanel
        info={suspended}
        phone={phone.trim()}
        password={password}
        onBack={() => {
          setSuspended(null);
          setPassword('');
        }}
      />
    );
  }

  return (
    <form className="auth-form" onSubmit={submit} noValidate>
      <div className={`input-group${errors.phone ? ' has-error' : ''}`}>
        <label htmlFor="login-phone">{t('phone')} <span className="req" aria-hidden="true">*</span></label>
        <PhoneInput
          id="login-phone"
          autoComplete="username"
          placeholder={t('authPhonePlaceholder')}
          invalid={Boolean(errors.phone)}
          value={phone}
          onChange={(next) => {
            setPhone(next);
            revalidate('phone', next);
          }}
        />
        {errors.phone && <span className="field-error">{errors.phone}</span>}
      </div>

      <div className={`input-group${errors.password ? ' has-error' : ''}`}>
        <label htmlFor="login-password">{t('password')} <span className="req" aria-hidden="true">*</span></label>
        <PasswordField
          id="login-password"
          autoComplete="current-password"
          invalid={Boolean(errors.password)}
          value={password}
          onChange={(next) => {
            setPassword(next);
            revalidate('password', next);
          }}
        />
        {errors.password && <span className="field-error">{errors.password}</span>}
      </div>

      <div className="auth-row">
        <label className="check">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => {
              setRemember(e.target.checked);
              // Untick and it is gone straight away, not at the next sign-in the
              // visitor may never make from this machine.
              if (!e.target.checked) setRememberedPhone('');
            }}
          />
          <span className="box" aria-hidden="true">
            <i className="ri-check-line i" />
          </span>
          {t('authRememberMe')}
        </label>
      </div>

      <button className="btn btn-primary btn-block" disabled={login.isPending}>
        {login.isPending ? t('authLoggingIn') : t('authLogin')}
        <i className="ri-arrow-right-line i i-move" aria-hidden="true" />
      </button>
    </form>
  );
}

/** A 10-digit Indian mobile number, with the +91 the visitor never has to type. */
function PhoneInput({
  id,
  value,
  onChange,
  invalid,
  placeholder,
  autoComplete,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  invalid?: boolean;
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
}) {
  return (
    <div className="input-affix">
      <span className="prefix" aria-hidden="true">+91</span>
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete={autoComplete}
        maxLength={PHONE_LENGTH}
        placeholder={placeholder}
        className={`input${invalid ? ' invalid' : ''}`}
        aria-invalid={Boolean(invalid)}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(sanitizePhone(e.target.value))}
      />
    </div>
  );
}

/**
 * A first sign-in that must confirm the mobile number: a supervisor account is
 * typed in by their manager, so the number is proven here, by a code sent to it,
 * before the account can be used. The password goes again with the code, so the
 * code on its own is never a way in.
 */
function PhoneCheckPanel({
  phone,
  password,
  expiresInSec,
  onDone,
  onBack,
}: {
  phone: string;
  password: string;
  expiresInSec: number;
  onDone: (result: Parameters<ReturnType<typeof useFinishAuth>>[0]) => void;
  onBack: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(expiresInSec);

  useEffect(() => {
    if (left <= 0) return;
    const id = window.setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [left]);

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().length < 4 || left <= 0) return;
    setBusy(true);
    try {
      onDone(
        await api<Parameters<typeof onDone>[0]>('/auth/login/verify', {
          method: 'POST',
          body: { phone, password, code: code.trim() },
        }),
      );
    } catch (err) {
      setCode(''); // a wrong code is no use to retype from
      document.getElementById('login-code')?.focus();
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const r = await api<{ expiresInSec: number }>('/auth/login/resend', {
        method: 'POST',
        body: { phone, password },
      });
      setCode('');
      setLeft(Number(r.expiresInSec) || 120);
      toast(t('authCodeResent'), 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  };

  const mm = String(Math.floor(Math.max(0, left) / 60));
  const ss = String(Math.max(0, left) % 60).padStart(2, '0');

  return (
    <form className="auth-form" onSubmit={verify} noValidate>
      <div className="await-box">
        <i className="ri-shield-check-line" />
        <div>
          <b>{t('authConfirmNumberTitle')}</b>
          <p>{t('authConfirmNumberBody', { phone })}</p>
        </div>
      </div>
      <div className="input-group">
        <label htmlFor="login-code">{t('authCodeLabel')} <span className="req" aria-hidden="true">*</span></label>
        <input
          id="login-code"
          className="input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
          disabled={left <= 0}
        />
        <span className="input-help" role="status">
          {left > 0 ? t('authCodeExpiresIn', { time: `${mm}:${ss}` }) : t('authCodeExpired')}
        </span>
      </div>
      <button className="btn btn-primary btn-block" disabled={busy || left <= 0 || code.length < 4}>
        {t('authConfirmAndSignIn')}
        <i className="ri-arrow-right-line i i-move" aria-hidden="true" />
      </button>
      <div className="row between">
        <button type="button" className="btn ghost sm" onClick={onBack} disabled={busy}>
          {t('back')}
        </button>
        <button type="button" className="btn ghost sm" onClick={resend} disabled={busy}>
          {t('authResendCode')}
        </button>
      </div>
    </form>
  );
}

/**
 * Shown instead of letting a suspended account in: why it happened, and a way to
 * say something about it. The credentials are passed through from the login
 * attempt that just proved them — an appeal can't be made from a session,
 * because a suspended account never gets one.
 */
function SuspendedPanel({
  info,
  phone,
  password,
  onBack,
}: {
  info: SuspendedInfo;
  phone: string;
  password: string;
  onBack: () => void;
}) {
  const { t } = useT();
  const [writing, setWriting] = useState(false);
  const [message, setMessage] = useState('');
  const [doc, setDoc] = useState<{ url: string; name: string } | null>(null);
  const [sent, setSent] = useState(false);
  const appeal = useAppeal();
  const uploadDoc = useUploadAppealDoc();
  const toast = useToast();

  const pickDoc = async (file: File | undefined) => {
    if (!file) return;
    try {
      setDoc(await uploadDoc.mutateAsync({ file, phone, password }));
      toast(t('authDocAttached'), 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  };

  const send = async () => {
    try {
      const res = await appeal.mutateAsync({
        phone,
        password,
        message: message.trim(),
        document: doc ?? undefined,
      });
      setSent(true);
      toast(res.message, 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  };

  const pending = info.appealPending && !sent;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="auth-alert bad">
        <b>
          <i className="ri-shield-cross-line" /> {t('authSuspendedTitle')}
        </b>
        {info.reason ? (
          <p style={{ margin: 0 }}>
            <span className="muted">{t('authSuspendedReason')} </span>
            {info.reason}
          </p>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            {t('authSuspendedNoReason')}
          </p>
        )}
        {info.suspendedAt && (
          <span className="muted" style={{ fontSize: 12 }}>
            {t('authSuspendedOn', { date: dayFmt(info.suspendedAt) })}
          </span>
        )}
      </div>

      {/* A previous appeal that was turned down — say so, and let them try again
          with whatever the note asked for. */}
      {info.declinedNote && !pending && !sent && (
        <div className="auth-alert">
          <b>{t('authAppealDeclined')}</b>
          <p style={{ margin: 0 }}>{info.declinedNote}</p>
          {info.declinedAt && (
            <span className="muted" style={{ fontSize: 12 }}>
              {dayFmt(info.declinedAt)}
            </span>
          )}
        </div>
      )}

      {sent || pending ? (
        <p className="muted" style={{ margin: 0 }}>
          <i className="ri-time-line" />{' '}
          {info.appealSentAt && !sent
            ? t('authAppealPendingSent', { date: dayFmt(info.appealSentAt) })
            : t('authAppealPending')}
        </p>
      ) : writing ? (
        <div className="input-group">
          <label htmlFor="appeal-message">{t('authAppealLabel')}</label>
          <textarea
            id="appeal-message"
            autoFocus
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('authAppealPlaceholder')}
          />

          <label htmlFor="appeal-doc" style={{ marginTop: 8 }}>
            {t('authSupportingDoc')} <span className="muted">({t('authSupportingDocHint')})</span>
          </label>
          <input
            id="appeal-doc"
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            disabled={uploadDoc.isPending}
            onChange={(e) => pickDoc(e.target.files?.[0])}
          />
          <span className="muted" style={{ fontSize: 12 }}>
            {t('authSupportingDocTip')}
          </span>
          {uploadDoc.isPending && <span className="muted">{t('authUploading')}</span>}
          {doc && (
            <span style={{ color: 'var(--good)', fontSize: 13 }}>
              <i className="ri-check-line" /> {doc.name}
            </span>
          )}

          <div className="form-actions">
            <button type="button" className="btn ghost" onClick={() => setWriting(false)}>
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={appeal.isPending || uploadDoc.isPending || message.trim().length < 10}
              onClick={send}
            >
              {appeal.isPending ? t('authSending') : t('authSendAppeal')}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn-primary btn-block" onClick={() => setWriting(true)}>
          <i className="ri-mail-send-line" />{' '}
          {info.declinedNote ? t('authAppealAgain') : t('authAppeal')}
        </button>
      )}

      <button type="button" className="btn ghost btn-block" onClick={onBack}>
        {t('authBackToLogin')}
      </button>
    </div>
  );
}

function SignupFlow({ onDone, initialRole = 'user' }: { onDone: () => void; initialRole?: Role }) {
  const { t } = useT();
  const v = useValidators();
  const [step, setStep] = useState<SignupStep>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  // Seconds left on the current code. The server expires it and unlocks a resend
  // at the same moment, so this one countdown drives both buttons.
  const [resendIn, setResendIn] = useState(0);
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);
  const [signupToken, setSignupToken] = useState('');
  // The number the code box is for. "Change number" unlocks the box to fix a
  // typo or try another number.
  const [sentTo, setSentTo] = useState('');
  // When each number's code dies (ms). The server rations codes per number —
  // a new one only once the last has expired — so going back to a number whose
  // code is still good returns to that code with its own countdown, instead of
  // asking the server and being told to wait. A different number always gets
  // a fresh code straight away.
  const liveCodes = useRef(new Map<string, number>());
  const otpRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>(initialRole);

  /** Same rule as the login form: flag on submit, clear the moment it is fixed. */
  type Field = 'phone' | 'name' | 'email' | 'password' | 'idNumber';
  const [errors, setErrors] = useState<Partial<Record<Field, string | null>>>({});

  // Manager-only: government ID, reviewed by an admin before approval.
  const [idType, setIdType] = useState('aadhaar');
  const [idNumber, setIdNumber] = useState('');
  const [idFile, setIdFile] = useState<{ url: string; name: string } | null>(null);
  const idRule = ID_PROOF_RULES[idType] ?? { max: 32 };

  const check = (field: Field, value: string): string | null => {
    switch (field) {
      case 'phone':
        return v.phone(value);
      case 'name':
        return v.name(value, t('name'));
      case 'email':
        return v.optionalEmail(value);
      case 'password':
        return v.password(value);
      case 'idNumber': {
        const label = ID_PROOF_TYPES.find((it) => it.value === idType)?.label;
        return v.idNumber(value, idType, label ? t(label) : t('authIdNumber'));
      }
    }
  };

  const revalidate = (field: Field, value: string) =>
    setErrors((e) => (e[field] ? { ...e, [field]: check(field, value) } : e));
  useRecheckOnLanguage(setErrors, () => ({
    phone: check('phone', phone),
    name: check('name', name),
    email: check('email', email),
    password: check('password', password),
    idNumber: check('idNumber', idNumber),
  }));

  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();
  const signup = useSignup();
  const uploadId = useUploadIdProof();
  const toast = useToast();
  const finishAuth = useFinishAuth();

  const pickIdFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      setIdFile(await uploadId.mutateAsync({ file, signupToken }));
      toast(t('authIdUploaded'), 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  };

  const sendOtp = async () => {
    if (!isValidPhone(phone)) {
      setErrors((e) => ({ ...e, phone: v.phone(phone) }));
      return;
    }
    setErrors((e) => ({ ...e, phone: null }));
    const number = phone.trim();
    /** Back to the code box for `number`, whose code has `secs` left. */
    const resume = (secs: number) => {
      if (number !== sentTo) setCode('');
      setSentTo(number);
      setResendIn(secs);
      setStep('otp');
      toast(t('authOtpStillLive', { phone: number }));
      window.setTimeout(() => otpRef.current?.focus(), 0);
    };
    if (step === 'phone') {
      const left = Math.ceil(((liveCodes.current.get(number) ?? 0) - Date.now()) / 1000);
      if (left > 0) return resume(left);
    }
    try {
      const r = await requestOtp.mutateAsync({ phone: number });
      const secs = r.expiresInSec ?? 120;
      liveCodes.current.set(number, Date.now() + secs * 1000);
      setStep('otp');
      setSentTo(number);
      setCode('');
      setResendIn(secs);
      // While OTP_VIA_API is on (testing) the server returns the code — show it in
      // a snackbar and prefill the box so signup can be tested without SMS.
      if (r.otp) {
        setCode(r.otp);
        toast(t('authOtpIs', { code: r.otp }), 'good');
      } else {
        toast(t('authOtpSent', { phone }), 'good');
      }
    } catch (err) {
      // A code sent to this number earlier (another tab, before a reload) is
      // still good: the server says so rather than sending another.
      const wait = err instanceof ApiError && err.status === 429 && err.body.codeLive ? Number(err.body.retryAfterSec) : 0;
      if (wait > 0) {
        liveCodes.current.set(number, Date.now() + wait * 1000);
        return resume(wait);
      }
      toast((err as Error).message, 'bad');
    }
  };

  const verify = async () => {
    try {
      const r = await verifyOtp.mutateAsync({ phone: phone.trim(), code: code.trim() });
      setSignupToken(r.signupToken);
      setStep('details');
    } catch (err) {
      // A wrong code is no use to retype from: clear it, ready for the right one.
      setCode('');
      otpRef.current?.focus();
      toast((err as Error).message, 'bad');
    }
  };

  const createAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    const found: Partial<Record<Field, string | null>> = {
      name: check('name', name),
      email: check('email', email),
      password: check('password', password),
      // The ID box only exists for a manager, so only they can fail it.
      ...(role === 'manager' ? { idNumber: check('idNumber', idNumber) } : {}),
    };
    const form = e.currentTarget as HTMLFormElement;
    if (Object.values(found).some(Boolean)) {
      setErrors((prev) => ({ ...prev, ...found }));
      focusFirstInvalid(form);
      return;
    }
    setErrors({});

    // The number is handled above, under the field it belongs to; the upload
    // has nowhere of its own to speak, so it stays a toast.
    if (role === 'manager' && !idFile) {
      toast(t('authIdRequired'), 'warn');
      const file = form.querySelector<HTMLInputElement>('#signup-idfile');
      file?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      file?.focus({ preventScroll: true });
      return;
    }
    try {
      const result = await signup.mutateAsync({
        signupToken,
        name: name.trim(),
        address: address.trim(),
        email: email.trim(),
        password,
        role,
        idProof:
          role === 'manager' && idFile
            ? { type: idType, number: idNumber.trim(), url: idFile.url, name: idFile.name }
            : undefined,
      });
      finishAuth(result, onDone);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  };

  /**
   * Which account this is. Shown from the first step so a venue owner who came
   * through "List your venue" sees their choice already made; it only changes
   * what the details step asks for.
   */
  const rolePick = (
    <div className="input-group">
      <span className="group-label" id="signup-role-label">{t('authRolePickLabel')}</span>
      <div className="role-pick" role="radiogroup" aria-labelledby="signup-role-label">
        <label>
          <input type="radio" name="signup-role" value="user" checked={role === 'user'} onChange={() => setRole('user')} />
          <strong>{t('authRoleCustomerTitle')}</strong>
          <small>{t('authRoleCustomerHint')}</small>
        </label>
        <label>
          <input type="radio" name="signup-role" value="manager" checked={role === 'manager'} onChange={() => setRole('manager')} />
          <strong>{t('authRoleManagerTitle')}</strong>
          <small>{t('authRoleManagerHint')}</small>
        </label>
      </div>
    </div>
  );

  if (step === 'details') {
    return (
      <form className="auth-form" onSubmit={createAccount} noValidate>
        {rolePick}

        <p className="auth-verified">
          <i className="ri-checkbox-circle-fill" aria-hidden="true" /> {t('authVerified')} <b>+91 {phone}</b>
        </p>

        <div className={`input-group${errors.name ? ' has-error' : ''}`}>
          <label htmlFor="signup-name">{t('name')} <span className="req" aria-hidden="true">*</span></label>
          <input
            id="signup-name"
            name="name"
            autoComplete="name"
            className={`input${errors.name ? ' invalid' : ''}`}
            aria-invalid={Boolean(errors.name)}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              revalidate('name', e.target.value);
            }}
          />
          {errors.name && <span className="field-error">{errors.name}</span>}
        </div>

        <div className="input-group">
          <label htmlFor="signup-address">
            {t('authAddress')} <span className="muted">({t('optional')})</span>
          </label>
          <input
            id="signup-address"
            name="street-address"
            className="input"
            autoComplete="street-address"
            placeholder={t('authAddressPlaceholder')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        <div className={`input-group${errors.email ? ' has-error' : ''}`}>
          <label htmlFor="signup-email">
            {t('email')} <span className="muted">({t('optional')})</span>
          </label>
          {/* Sign-in here is mobile + password, so a browser that has saved credentials
              for this site holds the mobile number as its "username". These hints stop
              it filling that number into the field above the password box: the id/name
              say what the field is, and new-password marks the form as a registration
              rather than a login so no username is looked for at all. */}
          <input
            id="signup-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`input${errors.email ? ' invalid' : ''}`}
            aria-invalid={Boolean(errors.email)}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              revalidate('email', e.target.value);
            }}
          />
          {errors.email && <span className="field-error">{errors.email}</span>}
        </div>

        <div className={`input-group${errors.password ? ' has-error' : ''}`}>
          <label htmlFor="signup-password">{t('password')} <span className="req" aria-hidden="true">*</span></label>
          <PasswordField
            id="signup-password"
            name="new-password"
            autoComplete="new-password"
            invalid={Boolean(errors.password)}
            value={password}
            onChange={(next) => {
              setPassword(next);
              revalidate('password', next);
            }}
          />
          {errors.password && <span className="field-error">{errors.password}</span>}
        </div>

        {role === 'manager' && (
          <div className="auth-alert">
            <b>
              <i className="ri-id-card-line" aria-hidden="true" /> {t('authIdentityCheck')}
            </b>
            <p className="muted">{t('authIdentityCheckBody')}</p>

            <div className="input-group">
              {/* No htmlFor: Select renders its own button, not an <input> with this id. */}
              <span className="group-label">{t('authIdType')} <span className="req" aria-hidden="true">*</span></span>
              <Select
                value={idType}
                onChange={(next) => {
                  setIdType(next);
                  // Each document has its own length and character set, so a
                  // number typed for the previous one is not a shorter version
                  // of this one — it is the wrong number. Start the field over
                  // rather than silently truncating it into something plausible.
                  setIdNumber('');
                  setErrors((e) => ({ ...e, idNumber: null }));
                }}
                options={ID_PROOF_TYPES.map((it) => ({ value: it.value, label: t(it.label) }))}
                ariaLabel={t('authIdType')}
              />
            </div>

            <div className={`input-group${errors.idNumber ? ' has-error' : ''}`}>
              <label htmlFor="signup-idnumber">{t('authIdNumber')} <span className="req" aria-hidden="true">*</span></label>
              <input
                id="signup-idnumber"
                inputMode={idRule.digits ? 'numeric' : 'text'}
                maxLength={idRule.max}
                autoComplete="off"
                className={`input${errors.idNumber ? ' invalid' : ''}`}
                aria-invalid={Boolean(errors.idNumber)}
                value={idNumber}
                onChange={(e) => {
                  const next = sanitizeIdNumber(e.target.value, idType);
                  setIdNumber(next);
                  revalidate('idNumber', next);
                }}
                placeholder={t('authIdNumberPlaceholder')}
              />
              {errors.idNumber && <span className="field-error">{errors.idNumber}</span>}
            </div>

            <div className="input-group">
              <label htmlFor="signup-idfile">{t('authIdUpload')} <span className="req" aria-hidden="true">*</span></label>
              <input
                id="signup-idfile"
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                disabled={uploadId.isPending}
                onChange={(e) => pickIdFile(e.target.files?.[0])}
              />
              {uploadId.isPending && <span className="muted">{t('authUploading')}</span>}
              {idFile && (
                <span className="auth-ok">
                  <i className="ri-check-line" aria-hidden="true" /> {idFile.name}
                </span>
              )}
            </div>
          </div>
        )}

        <button className="btn btn-primary btn-block" disabled={signup.isPending || uploadId.isPending}>
          {t('authCreateAccountAction')}
          <i className="ri-arrow-right-line i i-move" aria-hidden="true" />
        </button>
      </form>
    );
  }

  return (
    <form
      className="auth-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (step === 'phone') void sendOtp();
        else void verify();
      }}
    >
      {rolePick}

      <div className={`input-group${errors.phone ? ' has-error' : ''}`}>
        <label htmlFor="signup-phone">{t('phone')} <span className="req" aria-hidden="true">*</span></label>
        <PhoneInput
          id="signup-phone"
          autoComplete="tel-national"
          placeholder={t('authPhoneHint')}
          invalid={Boolean(errors.phone)}
          value={phone}
          onChange={(next) => {
            setPhone(next);
            revalidate('phone', next);
          }}
          disabled={step === 'otp'}
        />
        {errors.phone ? (
          <span className="field-error">{errors.phone}</span>
        ) : (
          <span className="input-help">{t('authOtpIntro')}</span>
        )}
      </div>

      {step === 'phone' && (
        <button className="btn btn-primary btn-block" disabled={requestOtp.isPending}>
          {t('authSendOtp')}
          <i className="ri-arrow-right-line i i-move" aria-hidden="true" />
        </button>
      )}

      {step === 'otp' && (
        <>
          <div className="input-group">
            <label htmlFor="signup-otp">{t('authEnterOtp')} <span className="req" aria-hidden="true">*</span></label>
            <input
              ref={otpRef}
              id="signup-otp"
              className="input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder={t('authOtpPlaceholder')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <span className="input-help" role="status">
              {resendIn > 0
                ? t('authOtpExpiresIn', { time: `${Math.floor(resendIn / 60)}:${String(resendIn % 60).padStart(2, '0')}` })
                : t('authOtpExpired')}
            </span>
          </div>
          <button className="btn btn-primary btn-block" disabled={verifyOtp.isPending || resendIn <= 0}>
            {t('authVerify')}
            <i className="ri-arrow-right-line i i-move" aria-hidden="true" />
          </button>
          <div className="row between">
            {/* The number is locked while a code is out; this is the way back to fix a typo. */}
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => setStep('phone')}
            >
              {t('authChangeNumber')}
            </button>
            <button
              type="button"
              className="btn ghost sm"
              onClick={sendOtp}
              disabled={requestOtp.isPending || resendIn > 0}
            >
              {t('authResend')}
            </button>
          </div>
        </>
      )}
    </form>
  );
}
